import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, ShieldAlert, CheckCircle2, ClipboardCopy } from 'lucide-react';
import { toast } from 'sonner';
import { entityFieldCorruptionCensusAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

function copyLines(lines, label) {
  if (!lines.length) return;
  navigator.clipboard.writeText(lines.join('\n'))
    .then(() => toast.success(`Copied ${lines.length} ${label}`))
    .catch((err) => toast.error('Could not copy to clipboard: ' + getErrorMessage(err)));
}

export function EntityFieldCorruptionCensusPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await entityFieldCorruptionCensusAPI.run();
      setData(res);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    entityFieldCorruptionCensusAPI.run()
      .then(({ data: res }) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(getErrorMessage(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const corruption = data?.entity_field_corruption;
  const residue = data?.seed_repro_residue;
  const corruptionIds = (corruption?.collections || []).flatMap((c) => c.docs.map((d) => d.id));
  const residueIds = (residue?.collections || []).flatMap((c) => c.ids);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Entity Field Corruption Census</CardTitle>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-xs ml-auto"
            onClick={run}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            {data ? 'Re-run' : 'Run'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Prod-facing read-only census: (1) entity_id stored as a non-string BSON type on
          transactions/invoices, (2) repro/seed/test-prefixed id residue (checks both Mongo
          _id and the app-level id field) across every collection. No writes anywhere.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}

        {corruption && (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-xs font-medium text-muted-foreground">
                Entity ID Type Corruption
              </h4>
              <Badge
                variant={corruption.total_count > 0 ? 'destructive' : 'default'}
                className="text-[10px]"
                data-testid="entity-corruption-total"
              >
                {corruption.total_count} found
              </Badge>
              {corruption.total_count === 0 && <CheckCircle2 className="h-3 w-3 text-green-600" />}
              {corruptionIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 text-[10px] ml-auto px-1.5"
                  onClick={() => copyLines(corruptionIds, 'id(s)')}
                >
                  <ClipboardCopy className="h-3 w-3 mr-1" /> Copy IDs
                </Button>
              )}
            </div>
            {corruption.collections.map((c) => (
              <div key={c.collection} className="mb-2">
                <div className="text-xs font-mono">
                  {c.collection}: {c.total_count}
                  {c.truncated && (
                    <span className="text-amber-600"> (showing {c.returned_count}, truncated)</span>
                  )}
                </div>
                {c.docs.length > 0 && (
                  <div className="border rounded mt-1 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1 font-medium">_id</th>
                          <th className="text-left px-2 py-1 font-medium">BSON Type</th>
                          <th className="text-left px-2 py-1 font-medium">entity_id (raw)</th>
                          <th className="text-left px-2 py-1 font-medium">source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.docs.map((d) => (
                          <tr key={d.id} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono">{d.id}</td>
                            <td className="px-2 py-1">
                              <Badge variant="destructive" className="text-[10px]">{d.bson_type}</Badge>
                            </td>
                            <td className="px-2 py-1 font-mono">{d.entity_id_raw ?? '(null)'}</td>
                            <td className="px-2 py-1">{d.source || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {residue && (
          <div className="border-t pt-3">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-xs font-medium text-muted-foreground">
                Seed/Repro Residue ({residue.prefixes.join(', ')} prefixes)
              </h4>
              <Badge
                variant={residue.total_count > 0 ? 'destructive' : 'default'}
                className="text-[10px]"
                data-testid="residue-total"
              >
                {residue.total_count} found
              </Badge>
              {residue.total_count === 0 && <CheckCircle2 className="h-3 w-3 text-green-600" />}
              {residueIds.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 text-[10px] ml-auto px-1.5"
                  onClick={() => copyLines(residueIds, 'id(s)')}
                >
                  <ClipboardCopy className="h-3 w-3 mr-1" /> Copy IDs
                </Button>
              )}
            </div>
            {residue.collections.length === 0 ? (
              <p className="text-xs text-muted-foreground">No residue found in any collection.</p>
            ) : (
              residue.collections.map((c) => (
                <div key={c.collection} className="mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono">
                      {c.collection}: {c.total_count}
                      {c.truncated && (
                        <span className="text-amber-600"> (showing {c.returned_count}, truncated)</span>
                      )}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 text-[10px] px-1.5"
                      onClick={() => copyLines(c.ids, `${c.collection} id(s)`)}
                    >
                      <ClipboardCopy className="h-3 w-3 mr-1" /> Copy
                    </Button>
                  </div>
                  <div className="text-xs font-mono bg-muted/50 rounded p-1.5 mt-1 max-h-32 overflow-y-auto">
                    {c.ids.map((id) => <div key={id}>{id}</div>)}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
